import React, { useEffect, useRef, useState } from 'react';
import AddFormButton from './AddFormButton';
import FormCard from './FormCard';
import FieldOptionsPanel from './FieldOptionsPanel';
import { applyPresetMeta, fieldHasDuplicateLabelAndType, formHasDuplicateLabelAndType, formHasOptionFieldMissingOptions, missingOptionsMessage, optionFieldMissingOptions, isLockedStatusField, isStatusField } from '../../data/formFieldPresets';

function dependsOnFieldLabels(currentField, fields = []) {
  const seenLabels = new Set();
  const labels = [];
  fields.forEach((field) => {
    if (String(field.id) === String(currentField?.id)) return;
    if (isStatusField(field)) return;
    const label = String(field.fieldLabel || field.label || '').trim();
    if (!label) return;
    const labelKey = label.toLowerCase();
    if (seenLabels.has(labelKey)) return;
    const hasOptions = (field?.options || []).some((option) => String(option || '').trim());
    if (!hasOptions) return;
    seenLabels.add(labelKey);
    labels.push(label);
  });
  return labels;
}

const LABELS = ['Actions', 'Field Label', 'Field Type', 'Required', 'Field Access', 'Hide for', 'DependsOn', 'Options'];

function withFieldDefaults(field, index) {
  return {
    ...field,
    id: field.id ?? `${Date.now()}-${index}`,
    order: index + 1,
    fieldLabel: field.fieldLabel ?? field.name ?? '',
    fieldType: field.fieldType ?? field.typeLabel ?? 'Custom',
    required: Boolean(field.required),
    fieldAccess: field.fieldAccess ?? '',
    hideFor: field.hideFor ?? [],
    dependsOn: field.dependsOn ?? '',
    options: field.options ?? [],
    parentMappings: field.parentMappings ?? {},
    optionColors: field.optionColors ?? {},
    lockMode: field.lockMode,
    isNew: Boolean(field.isNew),
  };
}

function applyPresetsToFields(list = []) {
  const fieldsWithDefaults = list.map(withFieldDefaults);
  return fieldsWithDefaults.map((field) => applyPresetMeta(field, fieldsWithDefaults));
}

export default function FormCardGrid({
  initialFields,
  onChange,
  addExternalFieldRef,
  fieldTypeOptions = [],
  fieldAccessOptions = [],
  hideForOptions = [],
  // savingFieldId = null, // unused: never set by FormFieldsStep
  // deletingFieldId = null, // unused: FormCard isDeleting is not passed
  onSave,
  onDelete,
}) {
  const [fields, setFields] = useState(() => applyPresetsToFields(initialFields ?? []));
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [optionNotice, setOptionNotice] = useState('');
  const syncedSignatureRef = useRef('');
  const cardsScrollerRef = useRef(null);
  const cardNodeRefs = useRef({});
  const commit = (nextFields) => {
    const presetFields = applyPresetsToFields(nextFields);
    setFields(presetFields);
    onChange?.(presetFields);
  };

  const fieldSignature = (initialFields ?? []).map((field) => field.id).join(',');

  useEffect(() => {
    if (!optionNotice) return undefined;
    const timer = window.setTimeout(() => setOptionNotice(''), 3200);
    return () => window.clearTimeout(timer);
  }, [optionNotice]);

  useEffect(() => {
    if (!fieldSignature || fieldSignature === syncedSignatureRef.current) return;
    syncedSignatureRef.current = fieldSignature;
    setFields(applyPresetsToFields(initialFields));
  }, [fieldSignature, initialFields]);

  useEffect(() => {
    if (!addExternalFieldRef) return undefined;
    addExternalFieldRef.current = (fieldToAdd) => {
      if (!fieldToAdd) return;
      setFields((currentFields) => {
        const existing = currentFields.find((field) => (
          fieldHasDuplicateLabelAndType(fieldToAdd, [field])
        ));
        if (formHasDuplicateLabelAndType(currentFields) || existing) {
          if (existing) setSelectedFieldId(existing.id);
          return currentFields;
        }
        if (formHasOptionFieldMissingOptions(currentFields)) {
          return currentFields;
        }
        const nextFields = applyPresetsToFields([
          ...currentFields,
          { ...fieldToAdd, id: `${fieldToAdd.id}-${Date.now()}`, isNew: true },
        ]);
        const nextField = nextFields[nextFields.length - 1];
        setSelectedFieldId(nextField.id);
        onChange?.(nextFields);
        return nextFields;
      });
    };
    return () => {
      addExternalFieldRef.current = null;
    };
  }, [addExternalFieldRef, onChange]);

  useEffect(() => {
    if (selectedFieldId == null) return undefined;
    const scroller = cardsScrollerRef.current;
    const card = cardNodeRefs.current[selectedFieldId];
    if (!scroller || !card) return undefined;

    const frame = requestAnimationFrame(() => {
      const scrollerRect = scroller.getBoundingClientRect();
      const itemRect = card.getBoundingClientRect();
      const pad = 12;
      const fullyVisible =
        itemRect.left >= scrollerRect.left + pad &&
        itemRect.right <= scrollerRect.right - pad;
      if (fullyVisible) return;

      scroller.scrollTo({
        left: scroller.scrollLeft + itemRect.left - scrollerRect.left - (scrollerRect.width - itemRect.width) / 2,
        behavior: 'smooth',
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [selectedFieldId, fields]);

  const reorderFields = (fromId, toId) => {
    if (fromId == null || toId == null || String(fromId) === String(toId)) return;
    const fromIndex = fields.findIndex((field) => String(field.id) === String(fromId));
    const toIndex = fields.findIndex((field) => String(field.id) === String(toId));
    if (fromIndex < 0 || toIndex < 0) return;
    const nextFields = [...fields];
    const [moved] = nextFields.splice(fromIndex, 1);
    nextFields.splice(toIndex, 0, moved);
    commit(nextFields.map((item, index) => ({ ...item, order: index + 1 })));
  };
  const updateField = (updatedField) => {
  const current = fields.find((field) => field.id === updatedField.id);
  const fieldTypeChanged = current && current.fieldType !== updatedField.fieldType;
  if (updatedField.dependsOn && updatedField.dependsOn !== current?.dependsOn) {
    setSelectedFieldId(updatedField.id);
  }
  const parentLabel = String(current?.fieldLabel || '').trim();

  // Detect a single option rename (one option removed + one added, same count)
  const oldOptions = current?.options || [];
  const newOptions = updatedField.options || [];
  const removedOptions = oldOptions.filter((item) => !newOptions.includes(item));
  const addedOptions = newOptions.filter((item) => !oldOptions.includes(item));
  const renamedOption = (
    oldOptions.length === newOptions.length
    && removedOptions.length === 1
    && addedOptions.length === 1
  ) ? { from: removedOptions[0], to: addedOptions[0] } : null;

  commit(fields.map((field) => {
    if (field.id === updatedField.id) return updatedField;
    if (fieldTypeChanged && parentLabel && String(field.dependsOn || '').trim() === parentLabel) {
      return { ...field, dependsOn: '', parentMappings: {} };
    }
    if (renamedOption && parentLabel && String(field.dependsOn || '').trim() === parentLabel) {
      const remapped = Object.fromEntries(
        Object.entries(field.parentMappings || {}).map(([child, parent]) => [
          child,
          parent === renamedOption.from ? renamedOption.to : parent,
        ]),
      );
      return { ...field, parentMappings: remapped };
    }
    return field;
  }));
};
  const deleteField = async (id) => {
    const field = fields.find((item) => item.id === id);
    if(!field || isLockedStatusField(field)) return;
    try{
      const removed = await onDelete?.(field);
      if(removed === false) return;
      const deletedLabel = String(field.fieldLabel || '').trim();
      commit(fields
        .filter((item) => item.id !== id)
        .map((item, index) => {
          if (deletedLabel && String(item.dependsOn || '').trim() === deletedLabel) {
            return { ...item, dependsOn: '', parentMappings: {}, order: index + 1 };
          }
          return { ...item, order: index + 1 };
        }));
    } catch {
      // Keep the field in the grid; delete API error is stored on formFields.error.
    }
  }
  const addField = () => {
    if (formHasDuplicateLabelAndType(fields)) return;
    if (formHasOptionFieldMissingOptions(fields)) {
      return;
    }
    commit(applyPresetsToFields([
      ...fields,
      { id: `${Date.now()}-${fields.length}`, fieldLabel: '', fieldType: fieldTypeOptions[0] ?? '', options: [], isNew: true },
    ]));
  };
  const saveField = async (field) => {
    if (fieldHasDuplicateLabelAndType(field, fields)) {
      return null;
    }
    try {
      const saved = await onSave?.(field);
      if (!saved) return null;
      setFields((currentFields) => {
        const nextFields = applyPresetsToFields(currentFields.map((item) => (
          item.id === field.id
            ? { ...saved, isNew: false, order: item.order || 1 }
            : item
        )));
        onChange?.(nextFields);
        return nextFields;
      });
      return saved;
    } catch {
      return null;
    }
  };
  const hasUnresolvedDuplicateField = formHasDuplicateLabelAndType(fields);
  const hasMissingOptions = formHasOptionFieldMissingOptions(fields);
  const duplicateLabelAndTypeMessage = hasUnresolvedDuplicateField
    ? 'A field with this label and type already exists'
    : '';
  const missingOptionsText = missingOptionsMessage(fields);
  const selectedField = fields.find((field) => field.id === selectedFieldId) || null;

  return (
    <>
    {duplicateLabelAndTypeMessage || missingOptionsText || optionNotice ? (
      <div className="ca-validation-toast" role="alert">{duplicateLabelAndTypeMessage || missingOptionsText || optionNotice}</div>
    ) : null}
    <div className="ca-form-card-grid__container ">
    <div className="ca-form-card-grid">
      <div className="ca-form-card-grid__labels">
        {LABELS.map((label) => <div key={label} className="ca-form-card-grid__label">{label}</div>)}
      </div>
      <div className="ca-form-card-grid__cards" ref={cardsScrollerRef}>
        {fields.map((field) => (
          <FormCard
            key={field.id}
            field={field}
            fieldOptions={dependsOnFieldLabels(field, fields)}
            fieldTypeOptions={fieldTypeOptions}
            fieldAccessOptions={fieldAccessOptions}
            hideForOptions={hideForOptions}
            // isSaving={savingFieldId === field.id}
            selected={selectedField?.id === field.id}
            isDragging={String(draggingId) === String(field.id)}
            cardRef={(node) => {
              if (node) cardNodeRefs.current[field.id] = node;
              else delete cardNodeRefs.current[field.id];
            }}
            onSelect={() => setSelectedFieldId(field.id)}
            onDragStartField={() => setDraggingId(field.id)}
            onDragEndField={() => setDraggingId(null)}
            onReorder={reorderFields}
            // isDeleting={deletingFieldId === field.id}
            onChange={updateField}
            onDelete={deleteField}
            onSave={saveField}
            onOptionNotice={setOptionNotice}
            hasDuplicateIdentity={fieldHasDuplicateLabelAndType(field, fields)}
            hasMissingOptions={optionFieldMissingOptions(field)}
          />
        ))}
        <AddFormButton onAdd={addField} disabled={hasUnresolvedDuplicateField || hasMissingOptions} />
      </div>
        
    </div>
        <FieldOptionsPanel formFields={fields} selectedField={selectedField} onChange={commit} />

        </div>
    </>
  );
}